import { apiCall, ApiError } from '@/lib/api/api-helpers'
import { useJobStore } from '@/stores/jobStore'
import type { JobType } from '@genieartinc/schemas'

interface SubmissionIdentity { type: JobType; canvasId: string; nodeId: string }
interface JobAcknowledgement { success: boolean; data?: { jobId: string; createdAt: string; [key: string]: unknown } }

/** One visible receipt from click through acknowledgement, including a failed POST. */
export async function submitTrackedJob<T extends JobAcknowledgement>(payload: SubmissionIdentity & Record<string, unknown>): Promise<T> {
  const requestId = `request-${crypto.randomUUID()}`
  const createdAt = new Date().toISOString()
  const identity = { type: payload.type, canvasId: payload.canvasId, nodeId: payload.nodeId }
  useJobStore.getState().addJob({ id: requestId, requestId, ...identity, status: 'PENDING', submission: 'submitting', message: 'Submitting', createdAt })
  try {
    const result = await apiCall('/api/jobs/create', true, {
      method: 'POST', body: JSON.stringify({ ...payload, requestId }),
      headers: { 'x-request-id': requestId }, signal: AbortSignal.timeout(30_000),
    }) as T
    if (!result.success || !result.data?.jobId) throw new Error('The server did not confirm a job ID.')
    useJobStore.getState().acknowledgeSubmission(requestId, {
      id: result.data.jobId, requestId, ...identity, status: 'PENDING', progress: 0,
      message: 'Queued', createdAt: result.data.createdAt || createdAt,
    })
    return result
  } catch (error) {
    // A missing response is not proof that a paid job was rejected. Never auto-retry.
    const rejected = error instanceof ApiError && error.status >= 400 && error.status < 500
    useJobStore.getState().updateJobStatus(requestId, 'FAILED', {
      submission: rejected ? 'rejected' : 'unconfirmed',
      message: rejected ? 'Submission rejected' : 'Submission not confirmed',
      error: rejected && error instanceof Error ? error.message : 'The response was not received. Refresh Activity to check for accepted work before trying again.',
    })
    console.warn('Job submission did not complete', { requestId, canvasId: payload.canvasId, nodeId: payload.nodeId, status: rejected ? 'rejected' : 'unconfirmed' })
    throw error
  }
}
